import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import supabase from '../../supabase/supabase';
import * as FiIcons from 'react-icons/fi';

const {
  FiBookOpen,
  FiShield,
  FiFileText,
  FiArrowUpRight,
  FiCheckCircle,
  FiLink
} = FiIcons;

const integrations = [
  {
    name: 'QuickBooks',
    description:
      'Connect QuickBooks to import your accounting data into ProfitYO.',
    status: 'Not connected',
    action: 'Connect',
    icon: FiBookOpen,
    tone: 'emerald',
    key: 'quickbooks'
  },
  {
    name: 'Plaid',
    description:
      'Connect your bank accounts and securely sync bank transactions with ProfitYO.',
    status: 'Available',
    action: 'Manage',
    icon: FiShield,
    tone: 'violet',
    key: 'plaid'
  },
  {
    name: 'Wave Accounting',
    description:
      'Connect Wave to securely import your accounting data into ProfitYO.',
    status: 'Not connected',
    action: 'Connect',
    icon: FiFileText,
    tone: 'blue',
    key: 'wave'
  }
];

const iconToneClasses = {
  emerald: 'bg-emerald-50 text-emerald-700',
  violet: 'bg-violet-50 text-violet-700',
  blue: 'bg-blue-50 text-blue-700'
};

export default function Integrations() {
  const navigate = useNavigate();

  const [message, setMessage] = useState('');

  /*
   * ---------------------------------------------------------
   * SEPARATE CONNECTION STATES
   * ---------------------------------------------------------
   *
   * QuickBooks and Wave intentionally have their own
   * connection/loading states.
   *
   * Connecting Wave will NOT affect QuickBooks.
   * Connecting QuickBooks will NOT affect Wave.
   */
  const [isConnectingQuickBooks, setIsConnectingQuickBooks] =
    useState(false);

  const [isConnectingWave, setIsConnectingWave] =
    useState(false);

  const [activeOrganization, setActiveOrganization] =
    useState(null);

  const [isLoadingOrganization, setIsLoadingOrganization] =
    useState(true);

  /*
   * ---------------------------------------------------------
   * QUICKBOOKS STATE
   * ---------------------------------------------------------
   */
  const [quickBooksConnection, setQuickBooksConnection] =
    useState(null);

  const [isLoadingQuickBooks, setIsLoadingQuickBooks] =
    useState(true);

  /*
   * ---------------------------------------------------------
   * PLAID STATE
   * ---------------------------------------------------------
   *
   * We use bank_accounts to determine whether the organization
   * currently has active Plaid-connected bank accounts.
   *
   * We intentionally do NOT query plaid_items because it
   * contains sensitive Plaid access-token data.
   */
  const [plaidBankAccounts, setPlaidBankAccounts] =
    useState([]);

  const [isLoadingPlaid, setIsLoadingPlaid] =
    useState(true);

  /*
   * ---------------------------------------------------------
   * WAVE STATE
   * ---------------------------------------------------------
   *
   * We only query safe connection metadata.
   *
   * NEVER query:
   * - access_token_encrypted
   * - refresh_token_encrypted
   */
  const [waveConnection, setWaveConnection] =
    useState(null);

  const [isLoadingWave, setIsLoadingWave] =
    useState(true);

  /*
   * ---------------------------------------------------------
   * Load organization and integration status
   * ---------------------------------------------------------
   */
  useEffect(() => {
    loadOrganization();
  }, []);

  /*
   * ---------------------------------------------------------
   * Handle OAuth result from callback
   * ---------------------------------------------------------
   */
  useEffect(() => {
    const params = new URLSearchParams(
      window.location.search
    );

    const waveStatus = params.get('wave');
    const waveMessage = params.get('message');

    if (waveStatus === 'connected') {
      setMessage(
        'Wave Accounting was connected successfully.'
      );

      /*
       * Reload the connection status so the newly-created
       * Wave connection appears immediately.
       */
      loadOrganization();

      /*
       * Remove OAuth query parameters from the URL
       * without navigating away from the page.
       */
      window.history.replaceState(
        {},
        document.title,
        window.location.pathname
      );
    }

    if (waveStatus === 'error') {
      setMessage(
        waveMessage ||
          'Unable to connect Wave Accounting.'
      );

      window.history.replaceState(
        {},
        document.title,
        window.location.pathname
      );
    }
  }, []);

  /*
   * ---------------------------------------------------------
   * Load the organization belonging to the currently
   * authenticated user.
   * ---------------------------------------------------------
   */
  const loadOrganization = async () => {
    setIsLoadingOrganization(true);

    /*
     * Each integration has its own loading state.
     */
    setIsLoadingQuickBooks(true);
    setIsLoadingPlaid(true);
    setIsLoadingWave(true);

    setMessage('');

    try {
      /*
       * -----------------------------------------------------
       * Get the currently authenticated ProfitYO user.
       * -----------------------------------------------------
       */
      const {
        data: { user },
        error: userError
      } = await supabase.auth.getUser();

      if (userError) {
        console.error(
          '[Integrations] Failed to get authenticated user:',
          userError
        );

        throw new Error(
          'Unable to verify your current session.'
        );
      }

      if (!user) {
        throw new Error(
          'You must be signed in to manage integrations.'
        );
      }

      /*
       * -----------------------------------------------------
       * Find the organization membership for this user.
       * -----------------------------------------------------
       */
      const {
        data,
        error: organizationError
      } = await supabase
        .schema('accounting')
        .from('organization_users')
        .select(`
          organization_id,
          role,
          organizations (
            id,
            name,
            type
          )
        `)
        .eq('user_id', user.id)
        .limit(1)
        .maybeSingle();

      if (organizationError) {
        console.error(
          '[Integrations] Organization lookup failed:',
          organizationError
        );

        throw new Error(
          'Unable to load your organization.'
        );
      }

      if (!data?.organization_id || !data?.organizations) {
        throw new Error(
          'No organization is associated with your account.'
        );
      }

      const organization = {
        id: data.organization_id,
        name: data.organizations.name,
        type: data.organizations.type,
        role: data.role
      };

      setActiveOrganization(organization);

      console.log(
        '[Integrations] Active organization:',
        organization
      );

      /*
       * -----------------------------------------------------
       * QUICKBOOKS
       *
       * Existing QuickBooks logic preserved.
       *
       * Do NOT select encrypted token columns.
       * -----------------------------------------------------
       */
      const {
        data: quickBooksData,
        error: quickBooksError
      } = await supabase
        .schema('accounting')
        .from('quickbooks_connections')
        .select(`
          id,
          company_name,
          environment,
          is_active
        `)
        .eq('organization_id', organization.id)
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();

      console.log(
        '[Integrations] QuickBooks connection query result:',
        {
          organizationId: organization.id,
          data: quickBooksData,
          error: quickBooksError
        }
      );

      if (quickBooksError) {
        console.error(
          '[Integrations] QuickBooks connection lookup failed:',
          quickBooksError
        );

        setQuickBooksConnection(null);

        throw new Error(
          'Unable to check QuickBooks connection status.'
        );
      }

      if (quickBooksData) {
        setQuickBooksConnection(quickBooksData);

        console.log(
          '[Integrations] QuickBooks is CONNECTED:',
          {
            id: quickBooksData.id,
            companyName:
              quickBooksData.company_name,
            environment:
              quickBooksData.environment,
            isActive:
              quickBooksData.is_active
          }
        );
      } else {
        setQuickBooksConnection(null);

        console.log(
          '[Integrations] No active QuickBooks connection found.'
        );
      }

      /*
       * -----------------------------------------------------
       * PLAID
       *
       * Existing logic preserved.
       *
       * We check bank_accounts rather than plaid_items.
       *
       * We NEVER select:
       * - plaid_items.access_token
       * - bank_accounts.plaid_access_token_encrypted
       * - bank_accounts.plaid_access_token_iv
       * -----------------------------------------------------
       */
      const {
        data: plaidBankData,
        error: plaidBankError
      } = await supabase
        .schema('accounting')
        .from('bank_accounts')
        .select(`
          id,
          institution_name,
          account_name,
          account_type,
          mask,
          plaid_item_id,
          plaid_account_id,
          is_active,
          last_sync_at
        `)
        .eq('organization_id', organization.id)
        .eq('is_active', true)
        .not('plaid_item_id', 'is', null);

      console.log(
        '[Integrations] Plaid bank account query result:',
        {
          organizationId: organization.id,
          data: plaidBankData,
          error: plaidBankError
        }
      );

      if (plaidBankError) {
        console.error(
          '[Integrations] Plaid bank account lookup failed:',
          plaidBankError
        );

        /*
         * Plaid failure must NOT affect QuickBooks.
         */
        setPlaidBankAccounts([]);

        console.warn(
          '[Integrations] Plaid status could not be loaded.'
        );
      } else {
        setPlaidBankAccounts(
          plaidBankData || []
        );

        console.log(
          '[Integrations] Plaid active bank accounts:',
          plaidBankData || []
        );
      }

      /*
       * -----------------------------------------------------
       * WAVE
       *
       * Completely separate from QuickBooks and Plaid.
       *
       * Only safe metadata is selected.
       * -----------------------------------------------------
       */
      const {
        data: waveData,
        error: waveError
      } = await supabase
        .schema('accounting')
        .from('wave_connections')
        .select(`
          id,
          wave_user_id,
          wave_business_id,
          business_name,
          access_token_expires_at,
          scope,
          is_active,
          last_synced_at,
          disconnected_at,
          created_at,
          updated_at
        `)
        .eq('organization_id', organization.id)
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();

      console.log(
        '[Integrations] Wave connection query result:',
        {
          organizationId: organization.id,
          data: waveData,
          error: waveError
        }
      );

      /*
       * Wave failure must NOT affect QuickBooks or Plaid.
       */
      if (waveError) {
        console.error(
          '[Integrations] Wave connection lookup failed:',
          waveError
        );

        setWaveConnection(null);

        console.warn(
          '[Integrations] Wave status could not be loaded.'
        );
      } else if (waveData) {
        setWaveConnection(waveData);

        console.log(
          '[Integrations] Wave is CONNECTED:',
          {
            id: waveData.id,
            businessName:
              waveData.business_name,
            businessId:
              waveData.wave_business_id,
            isActive:
              waveData.is_active
          }
        );
      } else {
        setWaveConnection(null);

        console.log(
          '[Integrations] No active Wave connection found.'
        );
      }
    } catch (error) {
      console.error(
        '[Integrations] Failed to load integration data:',
        error
      );

      setMessage(
        error?.message ||
          'Unable to load integration status.'
      );
    } finally {
      setIsLoadingOrganization(false);
      setIsLoadingQuickBooks(false);
      setIsLoadingPlaid(false);
      setIsLoadingWave(false);
    }
  };

  /*
   * ---------------------------------------------------------
   * Integration actions
   * ---------------------------------------------------------
   */
  const handleAction = async (integration) => {
    /*
     * -------------------------------------------------------
     * QUICKBOOKS
     *
     * QuickBooks has its OWN connecting state.
     * -------------------------------------------------------
     */
    if (integration.key === 'quickbooks') {
      /*
       * If already connected, don't start another OAuth
       * flow accidentally.
       */
      if (quickBooksConnection) {
        return;
      }

      setMessage('');
      setIsConnectingQuickBooks(true);

      try {
        if (!activeOrganization?.id) {
          throw new Error(
            'No active organization was found. Please refresh the page and try again.'
          );
        }

        console.log(
          '[Integrations] Starting QuickBooks connection:',
          {
            organizationId:
              activeOrganization.id,
            organizationName:
              activeOrganization.name
          }
        );

        /*
         * Start the QuickBooks OAuth flow.
         */
        const {
          data,
          error
        } = await supabase.functions.invoke(
          'quickbooks-connect',
          {
            body: {
              organizationId:
                activeOrganization.id,

              redirectPath:
                '/settings/integrations'
            }
          }
        );

        if (error) {
          console.error(
            '[Integrations] QuickBooks connect function error:',
            error
          );

          throw error;
        }

        console.log(
          '[Integrations] QuickBooks connect response:',
          data
        );

        const authorizationUrl =
          data?.authorizationUrl ||
          data?.authorization_url ||
          data?.authUrl ||
          data?.auth_url ||
          data?.url;

        if (!authorizationUrl) {
          throw new Error(
            'The QuickBooks authorization URL was not returned.'
          );
        }

        const destination =
          new URL(authorizationUrl);

        if (
          !['https:', 'http:'].includes(
            destination.protocol
          )
        ) {
          throw new Error(
            'The QuickBooks authorization URL is invalid.'
          );
        }

        /*
         * Redirect the browser to Intuit.
         */
        window.location.assign(
          destination.toString()
        );
      } catch (error) {
        console.error(
          '[Integrations] QuickBooks connection failed:',
          error
        );

        setMessage(
          error?.message ||
            'Unable to start QuickBooks connection.'
        );

        /*
         * Only QuickBooks connecting state is reset.
         */
        setIsConnectingQuickBooks(false);
      }

      return;
    }

    /*
     * -------------------------------------------------------
     * PLAID
     *
     * Existing logic preserved.
     * -------------------------------------------------------
     */
    if (integration.key === 'plaid') {
      navigate('/accounting/connect-bank');
      return;
    }

    /*
     * -------------------------------------------------------
     * WAVE
     *
     * Wave has its OWN connecting state.
     * -------------------------------------------------------
     */
    if (integration.key === 'wave') {
      /*
       * If already connected, don't start another OAuth
       * flow accidentally.
       */
      if (waveConnection) {
        return;
      }

      setMessage('');
      setIsConnectingWave(true);

      try {
        if (!activeOrganization?.id) {
          throw new Error(
            'No active organization was found. Please refresh the page and try again.'
          );
        }

        console.log(
          '[Integrations] Starting Wave connection:',
          {
            organizationId:
              activeOrganization.id,
            organizationName:
              activeOrganization.name
          }
        );

        /*
         * Start the Wave OAuth flow.
         */
        const {
          data,
          error
        } = await supabase.functions.invoke(
          'wave-connect',
          {
            body: {
              organizationId:
                activeOrganization.id,

              redirectPath:
                '/settings/integrations'
            }
          }
        );

        if (error) {
          console.error(
            '[Integrations] Wave connect function error:',
            error
          );

          throw error;
        }

        console.log(
          '[Integrations] Wave connect response:',
          data
        );

        const authorizationUrl =
          data?.authorizationUrl ||
          data?.authorization_url ||
          data?.authUrl ||
          data?.auth_url ||
          data?.url;

        if (!authorizationUrl) {
          throw new Error(
            'The Wave authorization URL was not returned.'
          );
        }

        const destination =
          new URL(authorizationUrl);

        /*
         * Wave OAuth must use HTTPS.
         */
        if (
          destination.protocol !==
          'https:'
        ) {
          throw new Error(
            'The Wave authorization URL is invalid.'
          );
        }

        /*
         * Redirect the browser to Wave.
         */
        window.location.assign(
          destination.toString()
        );
      } catch (error) {
        console.error(
          '[Integrations] Wave connection failed:',
          error
        );

        setMessage(
          error?.message ||
            'Unable to start Wave connection.'
        );

        /*
         * Only Wave connecting state is reset.
         */
        setIsConnectingWave(false);
      }

      return;
    }
  };

  /*
   * ---------------------------------------------------------
   * Render
   * ---------------------------------------------------------
   */
  return (
    <div className="min-h-full bg-slate-50/40">
      <div className="mx-auto max-w-[1180px] px-4 py-8 sm:px-6 lg:px-8">

        <header className="mb-8 rounded-[32px] border border-slate-200 bg-white px-6 py-7 shadow-sm md:px-8">
          <div className="flex items-start gap-4">

            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-lg shadow-slate-900/10">
              <SafeIcon
                icon={FiLink}
                className="text-lg"
              />
            </div>

            <div>
              <h1 className="text-2xl font-black tracking-tight text-slate-900">
                Integrations
              </h1>

              <p className="mt-1 max-w-xl text-sm leading-6 text-slate-500">
                Connect ProfitYO with the tools and services you use to manage your business.
              </p>

              {activeOrganization?.name && (
                <p className="mt-2 text-xs font-semibold text-slate-400">
                  Organization: {activeOrganization.name}
                </p>
              )}
            </div>

          </div>
        </header>

        {message && (
          <div
            role="status"
            aria-live="polite"
            className="mb-6 flex items-center gap-3 rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm font-semibold text-blue-800"
          >
            <SafeIcon
              icon={FiCheckCircle}
              className="shrink-0 text-blue-600"
            />

            {message}
          </div>
        )}

        <section
          aria-label="Available integrations"
          className="grid grid-cols-1 gap-5 lg:grid-cols-3"
        >
          {integrations.map((integration) => {

            /*
             * -------------------------------------------------
             * Integration type
             * -------------------------------------------------
             */
            const isQuickBooks =
              integration.key === 'quickbooks';

            const isPlaid =
              integration.key === 'plaid';

            const isWave =
              integration.key === 'wave';

            /*
             * -------------------------------------------------
             * QuickBooks state
             * -------------------------------------------------
             */
            const isQuickBooksConnected =
              isQuickBooks &&
              Boolean(quickBooksConnection);

            const isQuickBooksLoading =
              isQuickBooks &&
              isLoadingQuickBooks;

            /*
             * -------------------------------------------------
             * Plaid state
             * -------------------------------------------------
             */
            const isPlaidConnected =
              isPlaid &&
              plaidBankAccounts.length > 0;

            const isPlaidLoading =
              isPlaid &&
              isLoadingPlaid;

            /*
             * -------------------------------------------------
             * Wave state
             * -------------------------------------------------
             */
            const isWaveConnected =
              isWave &&
              Boolean(waveConnection);

            const isWaveLoading =
              isWave &&
              isLoadingWave;

            /*
             * -------------------------------------------------
             * Display status
             * -------------------------------------------------
             */
            const displayStatus =
              isQuickBooks
                ? isQuickBooksLoading
                  ? 'Checking...'
                  : isQuickBooksConnected
                  ? 'Available'
                  : 'Not connected'
                : isPlaid
                ? isPlaidLoading
                  ? 'Checking...'
                  : 'Available'
                : isWave
                ? isWaveLoading
                  ? 'Checking...'
                  : isWaveConnected
                  ? 'Available'
                  : 'Not connected'
                : integration.status;

            /*
             * -------------------------------------------------
             * Display action
             * -------------------------------------------------
             */
            const displayAction =
              isQuickBooks
                ? isQuickBooksLoading
                  ? 'Loading…'
                  : isQuickBooksConnected
                  ? 'Connected'
                  : 'Connect'
                : isPlaid
                ? isPlaidLoading
                  ? 'Loading…'
                  : 'Manage'
                : isWave
                ? isWaveLoading
                  ? 'Loading…'
                  : isWaveConnected
                  ? 'Connected'
                  : 'Connect'
                : integration.action;

            /*
             * -------------------------------------------------
             * Button disabled state
             * -------------------------------------------------
             *
             * IMPORTANT:
             *
             * QuickBooks only looks at:
             * - isConnectingQuickBooks
             * - isLoadingQuickBooks
             *
             * Wave only looks at:
             * - isConnectingWave
             * - isLoadingWave
             *
             * They cannot affect each other.
             */
            const isButtonDisabled =
              isQuickBooks
                ? (
                    isConnectingQuickBooks ||
                    isLoadingOrganization ||
                    isLoadingQuickBooks ||
                    !activeOrganization?.id ||
                    isQuickBooksConnected
                  )
                : isWave
                ? (
                    isConnectingWave ||
                    isLoadingOrganization ||
                    isLoadingWave ||
                    !activeOrganization?.id ||
                    isWaveConnected
                  )
                : false;

            return (
              <article
                key={integration.key}
                className="flex min-h-[245px] flex-col rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md"
              >
                <div className="flex items-start justify-between gap-4">

                  <div
                    className={`flex h-12 w-12 items-center justify-center rounded-2xl ${iconToneClasses[integration.tone]}`}
                  >
                    <SafeIcon
                      icon={integration.icon}
                      className="text-xl"
                    />
                  </div>

                  <SafeIcon
                    icon={FiArrowUpRight}
                    className="text-slate-300"
                  />

                </div>

                <h2 className="mt-5 text-lg font-black tracking-tight text-slate-900">
                  {integration.name}
                </h2>

                <p className="mt-2 flex-1 text-sm leading-6 text-slate-500">
                  {integration.description}
                </p>

                {/* QuickBooks connection information */}
                {isQuickBooksConnected &&
                  quickBooksConnection?.company_name && (
                    <p className="mt-2 text-xs font-semibold text-slate-400">
                      Connected to:{' '}
                      {quickBooksConnection.company_name}
                    </p>
                  )}

                {/* Plaid connection information */}
                {isPlaidConnected && (
                  <div className="mt-2 space-y-1">
                    {plaidBankAccounts
                      .slice(0, 2)
                      .map((bank) => (
                        <p
                          key={bank.id}
                          className="text-xs font-semibold text-slate-400"
                        >
                          Connected to:{' '}
                          {bank.institution_name ||
                            bank.account_name ||
                            'Bank account'}
                        </p>
                      ))}

                    {plaidBankAccounts.length > 2 && (
                      <p className="text-xs font-semibold text-slate-400">
                        + {plaidBankAccounts.length - 2} more bank accounts
                      </p>
                    )}
                  </div>
                )}

                {/* Wave connection information */}
                {isWaveConnected &&
                  waveConnection?.business_name && (
                    <p className="mt-2 text-xs font-semibold text-slate-400">
                      Connected to:{' '}
                      {waveConnection.business_name}
                    </p>
                  )}

                <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">

                  <span
                    className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[11px] font-bold ${
                      displayStatus === 'Available'
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                        : displayStatus === 'Checking...'
                        ? 'border-slate-200 bg-slate-50 text-slate-500'
                        : 'border-slate-200 bg-slate-50 text-slate-600'
                    }`}
                  >

                    <span
                      className={`h-1.5 w-1.5 rounded-full ${
                        displayStatus === 'Available'
                          ? 'bg-emerald-500'
                          : 'bg-slate-400'
                      }`}
                    />

                    {displayStatus}

                  </span>

                  <button
                    type="button"
                    onClick={() =>
                      handleAction(integration)
                    }
                    disabled={isButtonDisabled}
                    className="inline-flex items-center justify-center rounded-xl bg-slate-900 px-4 py-2.5 text-xs font-bold text-white shadow-sm transition-all hover:bg-slate-800 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {isQuickBooks &&
                    isConnectingQuickBooks
                      ? 'Connecting…'
                      : isWave &&
                        isConnectingWave
                      ? 'Connecting…'
                      : displayAction}
                  </button>

                </div>
              </article>
            );
          })}
        </section>
      </div>
    </div>
  );
}